import React, { useCallback, useState } from 'react';
import { ITheme, useTheme } from 'src/theme';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';

import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Text from '@components/Text';
import ContainerBlock from '@components/ContainerBlock';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import Segment from '@components/Segment';
import Heading from '@components/Heading';
import Input from '@components/Input';
import { postGetPersonalDetails, postRetrieveInformation } from '@api/index';
import { find, get, isEmpty } from 'lodash';
import { useAvivaDispatch } from '@redux/hook';
import { setLoadingState } from '@redux/slices/loadingSlice';
import useForm from '@utils/useForm';
import { validate } from './service';
import { AgentPersonalDetailRequest } from '@models/AgentPersonalDetailRequest.model';
import { updateAgentDetailResponseRaw } from '@redux/slices/MLTC/step5Slice';
import Divider from '@components/Divider';
import { mask } from '@utils/mask';

const marginSegment = css`
  margin-bottom: 15px;
`;

const FlexBox = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    flex-direction: row;
    align-items: baseline;
    div {
      width: 100%;
      margin-right: 20px;
    }
    button {
      max-width: 175px;
      white-space: nowrap;
    }
  }
`;

enum OPTION {
  OPTION1 = 'ARISTOTLE',
  OPTION2 = 'USER_PREFERRED',
  OPTION3 = 'NO_AGENT'
}

export interface AssignFinancialFormProps {
  onChange: (e: any) => void;
  onVerify: (agentCode: any, sourceOfBusiness: any) => void;
}

const dataMock = {
  "status": true,
  "responseErrorDetail": "Test",
  "totalPages": 3,
  "totalItems": 3,
  "logoutUser": false,
  "data": {
    "clientNum": "ClientNum-123",
    "clientType": "Test Client",
    "surname": "Test",
    "salutation": "Ms",
    "fullName": "",
    "nric": "A444444U",
    "dob": "24-09-1988",
    "gender": "M",
    "maritalStatus": "Single",
    "nationality": "SGPrean",
    "race": "SG",
    "homeNum": "44445",
    "mobileNum": "00009999000",
    "officeNum": "333",
    "faxNum": "223",
    "emailId": "Test@gmail.com",
    "residential_Addr_1": "addr 1",
    "residential_Addr_2": "addr 2",
    "residential_Addr_3": "add1212312",
    "residential_Addr_4": "addr 4",
    "residential_Addr_5": "addr 5",
    "residentialAddrCountry": "SG",
    "residentialAddrPostalCode": "444",
    "agentTerminatedFlag": "Y",
    "givename": null
  }
}

function AssignFinancialForm({ onChange, onVerify }: AssignFinancialFormProps) {
  const theme: ITheme = useTheme();
  const [selectedType, setSelectedType] = useState<OPTION>(OPTION.OPTION1);
  const [adviserName, setAdviserName] = useState<string>('');
  const [agentCode, setAgentCode] = useState<string>('');
  const [agentSourceBusiness, setAgentSourceBusiness] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const dispatch = useAvivaDispatch();

  const { values, errors, handleChange, patchValue, formValid } = useForm({ agentCode: '' }, () => { }, validate);

  const handleOnChanged = useCallback((event: any) => {
    console.log(`QnB ➤ handleOnChanged(event)`, { event });
    const { name } = event.target;
    setSelectedType(name);
    onChange(name)
  }, [onChange])

  const onClickVerify = useCallback(async (agentCode: any) => {
    console.log(`QnB ➤ onClickVerify(agentCode)`, { agentCode });
    setAgentCode(agentCode);
    dispatch(setLoadingState(true));
    try {
      const response = await postGetPersonalDetails(new AgentPersonalDetailRequest({ agentCode }));
      const data = get(response, 'data.data', null);
      // const data = get(dataMock, 'data', null);
      const responseInfo = await postRetrieveInformation({ 'sourceSystem': 'MLTC', 'loggedInUserId': 0, 'clientNum': data?.clientNum, 'agentNo': agentCode });
      const dataInfo = get(responseInfo, 'data', null);
      const agentCoy = find(dataInfo, { agentCoy: '1' });
      console.log({ responseInfo, dataInfo, agentCoy });

      dispatch(updateAgentDetailResponseRaw(data));
      console.debug('postGetPersonalDetails', { data, response });

      if (data?.fullName && !isEmpty(data?.fullName) && data?.agentTerminatedFlag !== 'Y') {
        setAdviserName(data?.fullName);
        onVerify(agentCode, agentCoy.sourceOfBusiness);
        setCodeError('');
      } else {
        setAdviserName('');
        onVerify('', '');
        setCodeError('Please enter a valid adviser code');
      }
      dispatch(setLoadingState(false));
    } catch (error) {
      setAdviserName('');
      setCodeError('Please enter a valid adviser code');
      dispatch(setLoadingState(false));
      console.error(error);
    }
  }, [onVerify]);



  return (
    <>
      <Heading variant="smallTitle" fontWeight={600} margin="0 0 15px"> Assign a Financial Adviser Representative </Heading>
      <Divider size={1} margin="0 0 30px" />
      <Text margin="0 0 30px"> Your financial adviser representative will be able to assist you and service your policy needs </Text>
      <CheckboxList>
        <Checkbox checked={selectedType === OPTION.OPTION1} variant='border' type='radio' name={OPTION.OPTION1} onChange={handleOnChanged}>
          <Text>Singlife to assign a Representative. No action required.</Text>
        </Checkbox>
        {selectedType === OPTION.OPTION1 && (
          <ContainerBlock padding={30} paddingTablet={30} background={theme.colors.grey[600]}>
            <BulletList margin="0 0 30px">
              <Bullet>
                <Text margin="0 0 5px">If you are an existing Singlife customer, we&apos;ll assign the latest active representative either from Singapore Life Ltd. or from our distribution partners who had sold our plans to you.<br />
                  <b>Please note:</b> For Disability Support Programme (DSP) members, we&apos;ll assign the DSP representative.</Text>
              </Bullet>
              <Bullet>
                <Text margin="0 0 30px">If you don&apos;t have an active representative or if you are new to Singlife, we&apos;ll assign a dedicated Singlife Relationship Consultant.</Text>
                <Text margin={0}>Once you&apos;ve completed your purchase, we&apos;ll email you the details of the Financial Adviser Representative who will be servicing your policy.</Text>

              </Bullet>
            </BulletList>
          </ContainerBlock>
        )}
        <Checkbox checked={selectedType === OPTION.OPTION2} variant='border' type='radio' name={OPTION.OPTION2} onChange={handleOnChanged}>
          <Text>I have a preferred Representative in mind.</Text>
        </Checkbox>
        {selectedType === OPTION.OPTION2 && (
          <>
            <Segment hasBorderTop className={marginSegment}>
              <Heading variant='label'>Please enter a valid financial adviser representative Singlife code </Heading>
              {!isEmpty(codeError) && <Bullet variant="error" > {codeError} </Bullet>}
              <FlexBox>
                
                <Input variant='button' type='number' buttonLabel='Verify' name="agentCode" value={agentCode} margin={0} onButtonClick={onClickVerify} />
                {/* <Button margin={0} label='Verify' /> */}
              </FlexBox>
            </Segment>
            {!isEmpty(adviserName) && (<ContainerBlock margin="0 0 15px 0" padding={30} paddingTablet="20px 30px" background={theme.colors.grey[600]}>
              <Heading marginTablet="0 0 15px" variant='label'>Your financial adviser representative</Heading>
              <Heading marginTablet="0 0 40px" variant='label' fontWeight={300}>{mask(adviserName)}</Heading>
              <Heading marginTablet="0" variant='label'>To help with all your policy needs, we’ve assigned your preferred financial adviser representative.</Heading>
            </ContainerBlock>)}
          </>
        )}
        <Checkbox checked={selectedType === OPTION.OPTION3} variant='border' type='radio' name={OPTION.OPTION3} onChange={handleOnChanged}>
          <Text>I don’t need a Representative.</Text>
        </Checkbox>
        {selectedType === OPTION.OPTION3 && (
          <ContainerBlock padding={30} paddingTablet={30} background={theme.colors.grey[600]}>
            <Text margin={0}>If you change your mind or need help with your policy, you can still contact us.</Text>
          </ContainerBlock>
        )}
      </CheckboxList>
    </>

  );
};

export default AssignFinancialForm;
